import React, { useMemo, useState } from 'react';
import { Dialog } from '@mui/material';
import FileSaver from 'file-saver';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import API from 'api/API';
import { OnboardingStatus, onboardingStatus, TrayOnboardingStatus } from 'api/Responses/SatoResponses';
import BackButton from 'components/BackButton/BackButton';
import Button from 'components/Button';
import { IS_DEMO_APP } from 'components/Navigation/Navigation.constants';
import Page from 'components/Page';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { useLanguage } from 'context/AppContext';
import useSatoSets from 'hooks/useSatoSets';
import useReviewSet from './hooks/useReviewSet';
import RejectConfirmationModal from './molecules/RejectConfirmationModal';
import TrayCard from './molecules/TrayCard';
import * as styles from './SATOTraysPage.styles';

type SATOTraysPageParams = {
  setCode: string;
};

const SATOTraysPage: React.FC = () => {
  const [language] = useLanguage();
  const navigate = useNavigate();
  const [rejecting, setRejecting] = useState(false);
  const [pictureListLoading, setPictureListLoading] = useState<boolean>(false);
  const { setCode = '' } = useParams<SATOTraysPageParams>();
  const { allDrafts } = useSatoSets();
  const reviewSetObject = useReviewSet(allDrafts, setCode);

  const approveEnabled = useMemo(
    () =>
      reviewSetObject?.set.trays.every(tray =>
        [TrayOnboardingStatus.APPROVED, TrayOnboardingStatus.SKIPPED].includes(tray.status),
      ) && reviewSetObject?.set.trays.some(tray => tray.status === TrayOnboardingStatus.APPROVED),
    [reviewSetObject?.set.trays],
  );

  /**
   * This object may be undefined if this page is navigated-to directly.
   * Let's make sure it's defined before destructuring it!
   */
  if (!reviewSetObject) {
    return <></>;
  }
  const { set, loading, approveSet, rejectSet, updateSetData } = reviewSetObject;

  const getPictureList = async () => {
    setPictureListLoading(true);
    try {
      const pdfURI = await API.set.getSetPDF(set.code);
      FileSaver.saveAs(pdfURI, `${set.code}_DRAFT_PICTURE_LIST`);
    } catch (e) {
      toast.error(language.DraftActions.pictureListError);
    }
    setPictureListLoading(false);
  };

  const handleCloseModal = (): void => setRejecting(false);

  const handleReject = async (): Promise<void> => {
    handleCloseModal();
    await rejectSet();
  };

  return (
    <Page>
      {/* The actual page content */}
      <styles.Header>
        <BackButton onClick={() => navigate('/sato')} />
        <styles.Title>{set.display_code}</styles.Title>

        <styles.Status status={onboardingStatus[set.status]}>{language.Sato.status[set.status]}</styles.Status>
        <styles.ButtonContainer>
          {!IS_DEMO_APP && (
            <Button onClick={() => getPictureList()}>
              {!pictureListLoading ? language.DraftActions.getPictureList : <ClipLoader color={'white'} size={25} />}
            </Button>
          )}
          <Button
            onClick={updateSetData}
            disabled={
              ![OnboardingStatus.DRAFT, OnboardingStatus.REJECTED, OnboardingStatus.PENDING].includes(set.status)
            }
          >
            {language.Sato.updateStatus.updateSet}
          </Button>
          <Button onClick={approveSet} disabled={!approveEnabled}>
            {language.Sato.updateStatus.approveSet}
          </Button>

          <Button onClick={() => setRejecting(true)}>{language.Sato.updateStatus.rejectSet}</Button>
        </styles.ButtonContainer>
      </styles.Header>

      <styles.TraysContainer>
        {set.trays.map((tray, i) => (
          <TrayCard key={tray.tray_id} setCode={setCode} tray={tray} />
        ))}
      </styles.TraysContainer>

      <RejectConfirmationModal open={rejecting} onClose={handleCloseModal} onReject={handleReject} />

      {/* Loading spinner */}
      <Dialog open={loading}>
        <PageSpinner />
      </Dialog>
    </Page>
  );
};

export default SATOTraysPage;
